(ns livefeed.core
  (:require
   ["@yleisradio/tunnus-sdk" :default create-tunnus]
   ["@yleisradio/yds-components-react" :refer [YdsThemeProvider ToastProvider ToastNotifications]]
   ["react" :as react]
   ["smoothscroll-polyfill" :as smoothscroll]
   [cljs.core.async :refer [<! to-chan!] :refer-macros [go]]
   [clojure.string :as str]
   [goog.object :as gobj]
   [livefeed.analytics :as analytics]
   [livefeed.api :as api]
   [livefeed.tunnus :as tunnus]
   [livefeed.feed :as feed]
   [livefeed.feed-reactions :as feed-reactions]
   [livefeed.footer.footer :as footer]
   [livefeed.header :as header]
   [livefeed.i18n :refer [translate]]
   [livefeed.icons :as icons]
   [livefeed.message-status :as status :refer [ready-to-send?]]
   [livefeed.modal :as modal]
   [livefeed.moderation :as moderation]
   [livefeed.poll :as poll]
   [livefeed.properties :as properties :refer [get-property use-env!]]
   [livefeed.report-modal :as report-modal]
   [livefeed.state :as state]
   [livefeed.system-color-scheme :refer [system-color-scheme
                                         watch-color-scheme!]]
   [livefeed.toast :as toast]
   [livefeed.topic-container :as topic-container]
   [livefeed.topic-slideup :as topic-slideup]
   [livefeed.util :refer [format-emojis querystring->map rate-limit]]
   [livefeed.websocket :as websocket]
   [reagent.core :as reagent]
   [reagent.dom :as rdom]))

(defn flow-control-handler [{:keys [clear-to-send reason unbanned-at-epoch]}]
  (let [new-status
        (swap! status/status
               (fn [old-status]
                 (cond
                   (and (= :blocked old-status)
                        (not= :unblocked reason)) :blocked
                   (= :rejected old-status) old-status
                   (= :accepted old-status) old-status
                   clear-to-send :ready
                   :else reason)))]
    (when (and (= :blocked reason) (= :blocked new-status) (some? unbanned-at-epoch))
      (reset! status/unbanned-at (js/Date. (* 1000 unbanned-at-epoch))))
    (when (= :ready new-status)
      (reset! status/unbanned-at nil))
    new-status))

(defn notification-handler [notification]
  (when (= "moderating-comment" (:type notification))
    (status/sent-to-moderation!)
    (moderation/add-to-moderation! notification)))

(defn remove-message-handler [data]
  (when (= (:id @feed/quoted-message) data)
    (reset! feed/quoted-message nil))
  (feed/remove-message-by-id data))

(def *add-new-message
  (rate-limit
   feed/add-new-message
   {:min-interval 10 :max-interval 1200 :first-interval 100 :fast-queue-size 100}))

(defn message-handler [message]
  (*add-new-message message)
  (feed/handle-own-message-received message))

(defn- set-use-chat-public-input!
  "Switches between the widget's own input and chat-public's <message-input-embed>.
   The input unmounted by the switch never fires blur, so the focus flag is cleared here."
  [use-chat-public-input]
  (when (not= use-chat-public-input @state/use-chat-public-input)
    (reset! state/input-focused? false)
    (reset! state/use-chat-public-input use-chat-public-input)))

(def ^:private chat-public-flags
  "topic-state keys of the chat-public switches that only need their state atom updated."
  {:use-chat-public-report       state/use-chat-public-report
   :use-chat-public-description  state/use-chat-public-description
   :use-chat-public-reactions    state/use-chat-public-reactions
   :use-chat-public-message-feed state/use-chat-public-message-feed})

(defn state-change-handler [data]
  (case (:type data)
    "poll" (poll/update-poll! data)
    "poll-results" (poll/show-results! data)
    "topic-state" (do
                    (when-let [poll (first (:polls data))]
                      (poll/update-poll! poll))
                    (when (contains? data :use-chat-public-input)
                      (set-use-chat-public-input! (:use-chat-public-input data)))
                    (doseq [[k flag] chat-public-flags
                            :when (contains? data k)]
                      (reset! flag (get data k)))
                    (let [event (js/CustomEvent. "topic-state-change"
                                                 #js {:detail #js {:isLocked (:is-locked data)}})]
                      (.dispatchEvent js/window event))
                    (case (:is-locked data)
                      true (status/topic-locked!)
                      false (status/topic-unlocked!)
                      nil))
    (js/console.log (str "Unhandled state change " data))))

(defn comment-reaction-update-handler [data]
  (feed/update-message-likes (:comment-id data) (:likes data)))

(defn feed-reaction-update-handler [data]
  (feed-reactions/add-reactions! (:reaction-counts data)))

(defn user-role-update-handler [data]
  (reset! state/user data)
  (feed/process-pending-accepted-messages!))

(defn event-handler [event-id data]
  (case event-id
    :chat/new-message (message-handler data)
    :chat/labeled-message (message-handler data)
    :chat/flow-control (flow-control-handler data)
    :chat/notification (notification-handler data)
    :chat/state-change (state-change-handler data)
    :chat/comment-reaction-update (comment-reaction-update-handler data)
    :chat/feed-reaction-update (feed-reaction-update-handler data)
    :chat/removed-message (remove-message-handler data)
    :chat/user-role (user-role-update-handler data)
    :chsk/ws-ping nil
    (js/console.log (str "Unhandled message received " event-id " -- " data))))

(defn focus-handler []
  (when-not @websocket/connected?
    (websocket/reconnect!)))

(defn- tunnus-state-change! [^js user]
  (reset! state/tunnus-user {:nick (when user (.-nick user))})
  (reset! state/logged-in (some? user))
  (websocket/init-or-reconnect! event-handler))

(defn- tunnus-init []
  (let [tunnus-theme (case (properties/get-property :colorScheme) "dark" "dark" "light" "light" "auto")
        tunnus (create-tunnus (clj->js (merge (properties/get-property :tunnus)
                                              {:theme tunnus-theme
                                               :language (properties/get-property :language)})))
        channel (js/BroadcastChannel. "yle-tunnus")]
    (reset! state/tunnus tunnus)
    (reset! state/tunnus-broadcast-channel channel)
    (.catch
     (.then (.getUserInfo tunnus) tunnus-state-change!)
     #(tunnus-state-change! nil))
    (set! (.-onmessage channel)
          (fn [^js event]
            (let [message (.-data event)]
              (cond
                (= (.-type message) "tunnus-logged-in")
                (tunnus-state-change! (.-user message))

                (= (.-type message) "tunnus-logged-out")
                (tunnus-state-change! nil)

                (= (.-type message) "tunnus-user-info-changed")
                (tunnus-state-change! (.-user message))))))))

(defn ^:export disconnect []
  (websocket/disconnect!)
  (feed/reset-feed!))

(defn close-button [on-close]
  [:button.yle-livefeed__close-button {:on-click on-close
                                       :aria-label (translate :close-chat)}
   icons/close])

(defn theme-class-name [theme color-scheme]
  (str "yle-livefeed--theme-"
       theme
       (when color-scheme
         (str "-" color-scheme))))

(defonce component-params (reagent/atom {}))

(defn root []
  (let [blocked-modal-open? (reagent/atom false)
        open-blocked-modal #(reset! blocked-modal-open? true)
        close-blocked-modal #(reset! blocked-modal-open? false)
        topic-slideup-open? (reagent/atom
                             (and (topic-slideup/not-seen? (properties/get-property :chatId))
                                  (not @state/locked?)))
        on-open-topic-slideup (fn [open?]
                                (reset! topic-slideup-open? open?)
                                (topic-slideup/set-seen (properties/get-property :chatId)))
        message-filter (reagent/atom "all")
        on-change-message-filter (fn [filter]
                                   (analytics/track-event "chat" (str "message_filter_" filter "_selected"))
                                   (reset! message-filter filter)
                                   (reset! feed/at-feed-start? false)
                                   (when (= filter "best")
                                     (reset! feed/unseen-live-messages 0)))
        input-ref (react/createRef)
        focus-input #(some-> (.-current input-ref)
                             (.focus))
        on-close (when-let [cb (properties/get-property :onClose)]
                   (fn []
                     (analytics/track-event "chat" "closed")
                     (disconnect)
                     (rdom/unmount-component-at-node (.getElementById js/document "yle-livefeed"))
                     (cb)))]
    (fn []
      (let [best? (= @message-filter "best")
            chat-public-theme (or (properties/get-property :colorScheme) @system-color-scheme)]
        [:div.yle-livefeed {:class (theme-class-name (name @state/theme) (properties/get-property :colorScheme))}
         (when @state/topic-title
           [header/component
            @state/topic-title
            @message-filter
            (if @state/use-chat-public-description
              #(.dispatchEvent js/document (js/Event. "description-open"))
              #(on-open-topic-slideup (not @topic-slideup-open?)))
            on-close
            on-change-message-filter])
         (if @state/use-chat-public-message-feed
           [:message-feed-embed.yle-livefeed__chat-public-feed
            {"language" (properties/get-property :language)
             "base-url" (api/chat-public-url)
             "topic-id" @state/topic-id
             "theme" chat-public-theme}]
           [topic-container/component
            (cond
              @state/label @feed/labeled-messages
              best? @feed/best-messages
              :else @feed/messages-with-likes)
            best?
            (and (nil? @state/label) (not @websocket/connected?) (not @state/locked?))
            focus-input])
         (when-let [quoted-message @feed/quoted-message]
           [:div.yle-livefeed__quoted-message
            [:div.yle-livefeed__quoted-message__user
             (get-in quoted-message [:chat-user :nick])]
            [:button.yle-livefeed__quoted-message__close {:on-click   #(reset! feed/quoted-message nil)
                                                          :title      (translate :remove-quote)
                                                          :aria-label (translate :remove-quote)}
             icons/close]
            [:div.yle-livefeed__quoted-message__content
             (:content quoted-message)]])
         (when (nil? @state/label)
           [footer/component open-blocked-modal input-ref best?])

         (if @state/use-chat-public-description
           [:description-embed {"language" (properties/get-property :language)
                                "base-url" (api/chat-public-url)
                                "topic-id" @state/topic-id
                                "theme" chat-public-theme}]
           [topic-slideup/component
            @topic-slideup-open?
            #(on-open-topic-slideup false)
            @state/topic-title
            @state/topic-description])

         (cond
           @blocked-modal-open?
           [modal/blocked close-blocked-modal]

           (:id @feed/reported-message)
           (if @state/use-chat-public-report
             [:report-modal-embed {"language" (properties/get-property :language)
                                   "base-url" (api/chat-public-url)
                                   "topic-id" @state/topic-id
                                   "comment-id" (:id @feed/reported-message)
                                   "theme" chat-public-theme}]
             [report-modal/component])

           :else
           [poll/poll-modal])]))))

(def ^:private required-properties #{:chatId})
(def ^:private optional-properties #{:label :topicId :colorScheme :vrsDevice :vrsPlatform :language :initiating_app :env})

(defn props-from-querystring []
  (let [qs (.. js/window -location -search)
        qs-properties (querystring->map qs)]
    (select-keys qs-properties (concat required-properties optional-properties))))

(defn chat-id-from-path []
  (let [path (.. js/window -location -pathname)
        path-properties (re-find #"^/(.+)$" path)]
    (second path-properties)))

(defn validate-properties [properties]
  (doseq [property required-properties
          :when (not (contains? properties property))]
    (throw (js/Error. (str "Livefeed: required property " (name property) " not found"))))
  properties)

(defn ^:export connect []
  (go
    (let [chat-id (properties/get-property :chatId)]
      (<! (feed/load-initial-messages chat-id false))
      (when @state/best-view-enabled? (feed/load-initial-messages chat-id true))
      (websocket/init event-handler))))

(defonce ^:private report-listeners-registered?
  (do
    (.addEventListener js/document "report-closed"
                       (fn [_] (reset! feed/reported-message nil)))
    (.addEventListener js/document "report-submitted"
                       (fn [^js e]
                         (toast/notify! {:level "success"
                                         :hideCloseButton true
                                         :children (-> e .-detail .-message)})))
    true))

;; chat-public's <message-input> dispatches `require-nick` when a Tunnus
;; user without a nick tries to write.
(defonce ^:private require-nick-listener-registered?
  (do
    (.addEventListener js/document "require-nick"
                       (fn [_] (tunnus/require-nick!)))
    true))

(defn- in-public-message-input? [^js target]
  (boolean (and target (.closest target "message-input-embed textarea"))))

(defonce ^:private input-focus-listeners-registered?
  (do
    (.addEventListener js/document "focusin"
                       (fn [^js e]
                         (when (in-public-message-input? (.-target e))
                           (reset! state/input-focused? true))))
    (.addEventListener js/document "focusout"
                       (fn [^js e]
                         (when (in-public-message-input? (.-target e))
                           (reset! state/input-focused? false))))
    true))

(defn initialize-chat [chat-id]
  (go
    (let [chat (<! (api/get-chat chat-id))
          connected? @websocket/connected?]
      (if (= ::api/not-found chat)
        (status/not-found!)
        (let [{:keys [is-locked settings emojis title description use-chat-public-input use-chat-public-report use-chat-public-description use-chat-public-reactions use-chat-public-message-feed]} chat
              {:keys [theme feed-reactions best-view]
               :or   {theme "chat", best-view false}} settings
              theme (if (= "livefeed" theme) "chat" theme)]
          (reset! state/topic-description description)
          (reset! state/topic-title title)
          (reset! state/topic-id chat-id)
          (reset! state/use-chat-public-input use-chat-public-input)
          (reset! state/use-chat-public-report use-chat-public-report)
          (reset! state/use-chat-public-description use-chat-public-description)
          (reset! state/use-chat-public-reactions use-chat-public-reactions)
          (reset! state/use-chat-public-message-feed use-chat-public-message-feed)
          (reset! state/emojis (format-emojis emojis))
          (reset! state/theme theme)
          (reset! state/feed-reactions feed-reactions)
          (reset! state/best-view-enabled? best-view)
          (when is-locked
            (reset! state/locked? true)
            (status/topic-locked!))
          (properties/add-properties! {:chatId chat-id})
          (when connected?
            (disconnect))
          (<! (feed/load-initial-messages chat-id false))
          (when best-view (feed/load-initial-messages chat-id true))
          (when connected?
            (websocket/init event-handler))
          (analytics/track-page)
          (reset! state/initialized? true))))))

(defn observe-resize [elem]
  (let [connected? (atom true)
        callback (fn [entries]
                   (doseq [entry entries]
                     (let [rect (.-contentRect entry)]
                       (cond
                         (and (zero? (.-width rect))
                              (zero? (.-height rect))) (do (reset! connected? false)
                                                           (disconnect))
                         (not @connected?) (do (reset! connected? true)
                                               (connect))))))
        observer (js/ResizeObserver. callback)]
    (.observe observer elem)))

(defn providers [{:keys [colorScheme]} children]
  [:> YdsThemeProvider
   {:theme (cond
             (= "abitreenit" @state/theme)
             "light"
             :else
             (or colorScheme @system-color-scheme "light"))}
   [:> ToastProvider
    [:f> toast/toast-bridge]
    children
    [:> ToastNotifications]]])

(defn- is-chat-id? [maybe-id]
  (re-matches #"72-1-.+" maybe-id))

(defn- resolve-chat-id [params]
  (let [chat-id-or-name (:chatId params)]
    (if (is-chat-id? chat-id-or-name)
      (to-chan! [chat-id-or-name])
      (go
        (let [chat (<! (api/get-chat-by-path chat-id-or-name))]
          (if (= ::api/not-found chat)
            (do
              (status/not-found!)
              nil)
            (:id chat)))))))

(defn component [_params]
  (reagent/create-class
   {:component-did-mount  (fn [this]
                            (smoothscroll/polyfill)
                            (go
                              (let [[_ params] (reagent/argv this)
                                    {:keys [env label]} params
                                    _ (properties/use-env! (or (keyword env) :dev-local))
                                    chat-id (<! (resolve-chat-id params))
                                    new-properties (-> (select-keys params [:language :colorScheme :on-textfield-click :on-message-sent :onClose :vrsDevice :vrsPlatform])
                                                       (update :language (fnil keyword "fi"))
                                                       (merge {:chatId chat-id}))
                                    cleanup-color-scheme (watch-color-scheme!)]
                                (properties/add-properties! new-properties)
                                ;; Only initialize analytics once we have loaded
                                ;; all properties that they might need,
                                ;; including chat-id.
                                (analytics/init)
                                (gobj/set this "cleanup-color-scheme" cleanup-color-scheme)
                                (when label
                                  (reset! state/label label)
                                  (feed/poll-labeled-messages! label))
                                (let [reset-quoted-message-handler (fn [] (reset! feed/quoted-message nil))
                                      ;; Reply tapped on a chat-public-embedded message: chat-public's
                                      ;; <reply-button> dispatches `quote-message` with nick + content
                                      ;; in the detail. The `authorNick` field is the signal that this
                                      ;; event came from chat-public (not from the widget's own atom
                                      ;; watch, which puts the full message map under `:chat-user`).
                                      quote-message-handler (fn [^js e]
                                                              (let [detail (.. e -detail -new)
                                                                    author-nick (some-> detail .-authorNick)]
                                                                (when author-nick
                                                                  (let [comment-id (.-id detail)]
                                                                    (reset! feed/quoted-message
                                                                            {:id comment-id
                                                                             :comment-id comment-id
                                                                             :chat-user {:nick author-nick}
                                                                             :content (.-content detail)})))))]
                                  (gobj/set this "reset-quoted-message-handler" reset-quoted-message-handler)
                                  (gobj/set this "quote-message-handler" quote-message-handler)
                                  (.addEventListener js/document "reset-quoted-message" reset-quoted-message-handler)
                                  (.addEventListener js/document "quote-message" quote-message-handler)
                                  (go
                                    (<! (initialize-chat chat-id))
                                    (tunnus-init)
                                    (.addEventListener js/window "focus" focus-handler))))))
    :component-will-unmount (fn [this]
                              (when-let [bc @state/tunnus-broadcast-channel]
                                (.close bc)
                                (reset! state/tunnus-broadcast-channel nil))
                              (when-let [cleanup (gobj/get this "cleanup-color-scheme")]
                                (cleanup))
                              (when-let [handler (gobj/get this "reset-quoted-message-handler")]
                                (.removeEventListener js/document "reset-quoted-message" handler))
                              (when-let [handler (gobj/get this "quote-message-handler")]
                                (.removeEventListener js/document "quote-message" handler)))
    :component-did-update (fn [this [_ old-params]]
                            (let [[_ new-params] (reagent/argv this)]
                              (when (not= (:chatId old-params) (:chatId new-params))
                                (initialize-chat (:chatId new-params)))))
    :reagent-render       (fn [params]
                            (when @state/initialized?
                              [providers params
                               [root]]))
    :display-name         "YleChat"}))

(defn ^:dev/after-load mount []
  (let [target (.getElementById js/document "yle-livefeed")]
    (rdom/render [component @component-params] target)
    (observe-resize target)))

(defn merge-chat-id [props]
  (-> props
      (assoc :chatId (or (:topicId props) (:chatId props)))
      (dissoc :topicId)))

(defn- send-native-command [command & args]
  (when (and (exists? js/window.yleNative) (some? (.-sendCommand js/window.yleNative)))
    (let [bound-send-command (.bind (.-sendCommand js/window.yleNative) js/window.yleNative)]
      (apply bound-send-command command args))))

(defn merge-on-close-props [props]
  (if (contains? props :initiating_app)
    (assoc props :onClose
           (fn []
             (send-native-command "close")))
    props))

(defn add-script [type src]
  (let [script  (-> js/document
                    (.createElement "script"))]
    (-> script (.setAttribute  "type" type))
    (-> script (.setAttribute  "src" src))
    (-> js/document (.querySelector "head") (.appendChild script))))

(defn ^:export initialize [^js params]
  (let [path-chat-id (chat-id-from-path)
        new-params (-> {:chatId path-chat-id}
                       (merge (js->clj params :keywordize-keys true))
                       (select-keys [:chatId :topicId :language :env :label :newBackend :colorScheme :onClose :vrsDevice :vrsPlatform])
                       (merge (props-from-querystring)
                              (when (some-> js/window.yleNative .-onTextfieldClick)
                                {:on-textfield-click (fn on-textfield-click-wrapper [e]
                                                       (.preventDefault e)
                                                       (.stopPropagation e)
                                                       (if (and @state/logged-in (-> @state/tunnus-user :nick nil?))
                                                         (tunnus/require-nick!)
                                                         (.onTextfieldClick js/window.yleNative)))})
                              (when (some-> js/window.yleNative .-onMessageSent)
                                {:on-message-sent (fn on-message-sent-wrapper [success?]
                                                    (.onMessageSent js/window.yleNative success?))}))
                       (merge-chat-id)
                       (merge-on-close-props)
                       (validate-properties))]
    (reset! state/initialized? false)
    (reset! component-params new-params)
    (use-env! (:env new-params))
    (let [chat-public-url (get-property :chat-public-url)]
      (add-script "module" (str chat-public-url "/client.js"))
      (when (#{:dev-local :dev-test} (:env new-params))
        (add-script "module" (str chat-public-url "/@vite/client"))
        (add-script "module" (str chat-public-url "/ui/viteReload.js"))))

    (mount)))

(defn ^:export setChatId [id]
  (initialize-chat id)
  true)

(defn ^:export initializeWebview []
  (initialize (.-livefeedConfig js/window)))

(defn ^:export setMessage [message]
  (reset! state/input-value message)
  true)

(defn ^:export sendMessage [message]
  (reset! state/input-value message)
  (if (or (not (ready-to-send?)) (str/blank? message))
    false
    (let [success? (websocket/send-message message)]
      (when-let [on-message-sent (properties/get-property :on-message-sent)]
        (on-message-sent success?))
      (if success?
        (reset! state/input-value "")
        (status/send-failed))
      success?)))

(defn ^:export isAllowedToSend []
  (ready-to-send?))
